import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { CostCenter } from '@/lib/models/costCenter';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per l'endpoint dei CostCenters
 */
export interface CostCenterEndpoints {
	/**
	 * Recupera la lista dei centri di costo con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=name asc")
	 * @returns Promise con response OData contenente array di CostCenter
	 */
	getCostCenters(query: string): Promise<FetchResponse<ODataResponse<CostCenter>>>;

	/**
	 * Recupera un singolo centro di costo per ID
	 * @param id - ID del centro di costo
	 * @returns Promise con singolo CostCenter
	 */
	getCostCenter(id: number): Promise<FetchResponse<CostCenter>>;

	/**
	 * Aggiorna un centro di costo esistente
	 * @param id - ID del centro di costo da aggiornare
	 * @param payload - Dati da aggiornare (il backend ignora id e metadata)
	 * @returns Promise con CostCenter aggiornato
	 */
	updateCostCenter(id: number, payload: CostCenter): Promise<FetchResponse<CostCenter>>;

	/**
	 * Crea un nuovo centro di costo
	 * @param payload - Dati del nuovo centro di costo (il backend ignora id e metadata)
	 * @returns Promise con CostCenter creato
	 */
	postCostCenter(payload: CostCenter): Promise<FetchResponse<CostCenter>>;

	/**
	 * Elimina un centro di costo
	 * @param id - ID del centro di costo da eliminare
	 * @returns Promise con response di conferma
	 */
	deleteCostCenter(id: number): Promise<FetchResponse<void>>;
}

/**
 * Implementazione tipizzata degli endpoint per i CostCenters
 */
const costCenterEndpoints: CostCenterEndpoints = {
	getCostCenters(query: string): Promise<FetchResponse<ODataResponse<CostCenter>>> {
		return api.get(`/odata/v1/CostCenters${query}`);
	},

	getCostCenter(id: number): Promise<FetchResponse<CostCenter>> {
		return api.get(`/odata/v1/CostCenters/${id}`);
	},

	updateCostCenter(id: number, payload: CostCenter): Promise<FetchResponse<CostCenter>> {
		return api.put(`/api/v1/CostCenters/${id}`, payload);
	},

	postCostCenter(payload: CostCenter): Promise<FetchResponse<CostCenter>> {
		return api.post('/api/v1/CostCenters', payload);
	},

	deleteCostCenter(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/CostCenters/${id}`);
	},
};

export default costCenterEndpoints;
